import { mapValues } from 'es-toolkit';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { LumberGrade, LumberSpecies } from '@paragon/contract-library';
import { LumberOptionGroups } from '@paragon/data-connections';

type AllowedGradesBySpecies = { readonly [key in LumberSpecies]?: readonly LumberGrade[] };

type LumberOptionsState =
  | { readonly state: 'pending' }
  | { readonly state: 'loading' }
  | { readonly state: 'loaded'; readonly allowedGradesBySpecies: AllowedGradesBySpecies }
  | { readonly state: 'error'; readonly message: string };

const initialState: LumberOptionsState = { state: 'pending' };

const lumberOptionsSlice = createSlice({
  name: 'lumberOptions',
  initialState: initialState as LumberOptionsState,
  reducers: {
    lumberOptionsStartedLoading: () => ({ state: 'loading' as const }),
    lumberOptionsFailedLoading: (state, action: PayloadAction<string>) => ({ state: 'error', message: action.payload }),
    lumberOptionsFinishedLoading: (state, action: PayloadAction<LumberOptionGroups>) => {
      const allowedGradeSetsBySpecies: { [key in LumberSpecies]?: Set<LumberGrade> } = {};

      for (const lumberOptionGroup of action.payload) {
        for (const species of lumberOptionGroup.species) {
          const gradesForSpecies = allowedGradeSetsBySpecies[species];
          if (gradesForSpecies == null) {
            allowedGradeSetsBySpecies[species] = new Set(lumberOptionGroup.grades);
          } else {
            for (const grade of lumberOptionGroup.grades) {
              gradesForSpecies.add(grade);
            }
          }
        }
      }

      const allowedGradesBySpecies = mapValues(allowedGradeSetsBySpecies, (set) => [...set!.values()]);

      return { state: 'loaded', allowedGradesBySpecies };
    },
  },
});

export const { lumberOptionsStartedLoading, lumberOptionsFailedLoading, lumberOptionsFinishedLoading } =
  lumberOptionsSlice.actions;
export const lumberOptions = lumberOptionsSlice.reducer;
